import { useCallback, useEffect, useMemo, useState } from 'react';
import { localeUI } from '../lib/format';
import { useDialog } from '../lib/useDialog';
import { CalendarDays, Check, MapPin, Umbrella, X } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { NoCargo } from './NoCargo';
import { useFallo } from '../lib/useFallo';
import { ScheduleDayBoard, type BoardAppt, type Forecast } from './ScheduleDayBoard';

/**
 * Modal de agendamiento (port del "Schedule Inspection" de ClaimRoute). Muestra los
 * mejores slots que sugiere el motor por día (mejor inspector + hora + clima + lluvia% +
 * drive-time + millas), navegación por semana, "Pick manually" y tabs Inspector/Contractor/
 * No inspector. Sirve para agendar (mode 'book') y reagendar (mode 'reschedule').
 */
type Slot = {
  inspector_name: string; start: string; end: string; score: number;
  drive_in_min: number; added_miles: number; reason: string;
  weather_icon: string | null; weather_short: string | null; rain_pct: number;
};
type OptionsResp = {
  options: Slot[]; note: string | null; rainThreshold: number;
  week: { offset: number; from: string; to: string };
  clientName: string | null; contractorName: string | null; address: string | null;
  claimLoc?: { lat: number; lng: number }; durationMin?: number;
};
type Kind = 'inspector' | 'contractor' | 'none';

const fmtDay = (iso: string) => new Date(iso).toLocaleDateString(localeUI(), { weekday: 'short', month: 'short', day: 'numeric' });
const fmtTime = (iso: string) => new Date(iso).toLocaleTimeString(localeUI(), { hour: 'numeric', minute: '2-digit' });
// Clave de día en hora LOCAL (no UTC): así el agrupamiento y los headers coinciden con la hora
// que ve el usuario. Antes se usaba toISOString() (UTC) → en zonas detrás de UTC un slot de la
// mañana caía bajo el header del día ANTERIOR (clickeabas "21" y agendaba "22").
const dayKey = (iso: string) => { const d = new Date(iso); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const localInput = (d: Date) => { const p = (n: number) => String(n).padStart(2, '0'); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`; };

export function ScheduleModal({ claimId, appointmentId, mode = 'book', title, defaultType, urgent = false, onClose, onDone }: {
  claimId: string; appointmentId?: string; mode?: 'book' | 'reschedule';
  title?: string; defaultType?: string;
  /** "Urgent reschedule" (dueño 2026-10-19): la cita cancelada y el claim quedan con la etiqueta URGENT hasta agendar de nuevo. */
  urgent?: boolean;
  onClose: () => void; onDone: () => void;
}) {
  const { t } = useI18n();
  const dialogo = useDialog(onClose);
  const [kind, setKind] = useState<Kind>('inspector');
  const [manual, setManual] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0);
  const [resp, setResp] = useState<OptionsResp | null>(null);
  const [loading, setLoading] = useState(true);
  const { fallo, marcar, limpiar } = useFallo();
  const [inspectors, setInspectors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Manual pick / reschedule-later state
  const [manualInsp, setManualInsp] = useState('');
  const [manualDt, setManualDt] = useState(() => { const d = new Date(); d.setHours(9, 0, 0, 0); d.setDate(d.getDate() + 1); return localInput(d); });
  const [contractorName, setContractorName] = useState('');
  const [reason, setReason] = useState('');
  const [cause, setCause] = useState('');
  const [cancelLater, setCancelLater] = useState(false);
  // Slot elegido (paso de confirmación) + éxito.
  const [selected, setSelected] = useState<{ startIso: string; inspectorName?: string; label: string } | null>(null);
  // Día abierto en modo "Pick manually" (muestra el day-distribution board de ese día, aunque
  // aún no se haya elegido hora). Al hacer click en una columna/ventana → se vuelve `selected`.
  const [manualDay, setManualDay] = useState<Date | null>(null);
  const [booked, setBooked] = useState(false);
  // Datos del DayBoard embebido (para el día del slot elegido).
  const [boardAppts, setBoardAppts] = useState<BoardAppt[]>([]);
  const [forecast, setForecast] = useState<Forecast[]>([]);

  const load = useCallback(() => {
    setLoading(true);
    // Pasa el tipo → el motor usa la duración propia de ese tipo (atr 30min, check_pickup 15min…).
    const typeQ = defaultType ? `&type=${encodeURIComponent(defaultType)}` : '';
    api.get<OptionsResp>(`/api/claims/${claimId}/schedule-options?weekOffset=${weekOffset}${typeQ}`)
      .then((r) => { limpiar(); setResp(r); if (!manualInsp && r.options[0]) setManualInsp(r.options[0].inspector_name); })
      .catch((e) => { marcar(e); setResp(null); })
      .finally(() => setLoading(false));
  }, [claimId, weekOffset, manualInsp, defaultType]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get<{ inspectors: string[] }>('/api/scheduling/inspector-names').then((r) => setInspectors(r.inspectors)).catch(marcar); }, []);

  // Slots agrupados por día de la semana (7 días desde week.from).
  const byDay = useMemo(() => {
    const days: { key: string; iso: string; slots: Slot[] }[] = [];
    if (!resp) return days;
    // Iteramos por días en hora LOCAL (medianoche local + i), y agrupamos por clave local, para
    // que el header, la hora del slot y el "BOOK FOR" coincidan siempre en el mismo día.
    const base = new Date(resp.week.from);
    for (let i = 0; i < 7; i++) {
      const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() + i);
      const key = dayKey(d.toISOString());
      days.push({ key, iso: d.toISOString(), slots: (resp.options || []).filter((s) => dayKey(s.start) === key) });
    }
    return days;
  }, [resp]);

  const weekLabel = resp ? `${new Date(resp.week.from).toLocaleDateString(localeUI(), { month: 'short', day: 'numeric' })} – ${new Date(new Date(resp.week.to).getTime() - 86400000).toLocaleDateString(localeUI(), { month: 'short', day: 'numeric' })}` : '';
  const rainThreshold = resp?.rainThreshold ?? 60;
  const clientName = title || resp?.clientName || t('cr.visit');

  async function doBook(sel: { startIso: string; inspectorName?: string }) {
    setBusy(true); setErr(null);
    try {
      // Feasibility (avisa, no bloquea): solo cuando hay inspector asignado.
      const insp = sel.inspectorName ?? (kind === 'inspector' ? manualInsp : undefined);
      if (insp && (kind === 'inspector' || mode === 'reschedule')) {
        try {
          const f = await api.post<{ ok: boolean; reason: string | null; conflict: boolean }>(`/api/claims/${claimId}/check-slot`, { inspectorName: insp, startIso: sel.startIso, type: defaultType ?? null });
          if (!f.ok && !window.confirm(`${f.conflict ? t('sch.conflictWarn') : t('sch.tightWarn')}${f.reason ? `\n\n${f.reason}` : ''}\n\n${t('sch.bookAnyway')}`)) {
            setBusy(false); return;
          }
        } catch { /* best-effort: si falla el check, seguimos */ }
      }
      if (mode === 'reschedule' && appointmentId) {
        await api.post(`/api/appointments/${appointmentId}/reschedule`, { mode: 'now', startIso: sel.startIso, inspectorName: sel.inspectorName ?? null, reason: reason || 'rescheduled', cause: cause || null, urgent });
      } else {
        const body: Record<string, unknown> = { startIso: sel.startIso, assigneeKind: kind };
        if (defaultType) body.type = defaultType;
        if (kind === 'inspector') body.inspectorName = sel.inspectorName ?? manualInsp;
        if (kind === 'contractor') body.contractorName = contractorName || null;
        await api.post(`/api/claims/${claimId}/book`, body);
      }
      setBooked(true);
      setTimeout(onDone, 1100); // muestra el éxito antes de cerrar
    } catch (e) { setErr((e as Error).message || 'error'); setBusy(false); }
  }

  // El día del DayBoard: el del slot elegido, el abierto con "Pick manually", o —en el modo
  // "Set a specific date & time"— el día del datetime que se está eligiendo (para que TAMBIÉN
  // ahí se vea el calendario con los cálculos por inspector).
  //
  // OJO con `manualDt`: sale de un <input type="datetime-local"> y mientras se tipea llegan valores
  // a medio escribir ("2026-08-", un año de 4 dígitos incompleto). Eso da un Date inválido, y abajo
  // `toISOString()` sobre un Date inválido NO devuelve nada raro: TIRA RangeError. Como pasa en
  // render, se lo comía el ErrorBoundary y el modal entero se ponía en blanco mientras el usuario
  // escribía la fecha. Por eso el día se descarta si no es válido en vez de confiar en el input.
  const boardDay = useMemo(() => {
    const aMedianoche = (v: string | number | Date) => {
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return null;
      d.setHours(0, 0, 0, 0);
      return d;
    };
    if (selected) return aMedianoche(selected.startIso);
    if (manualDay) return aMedianoche(manualDay);
    if (manual && kind === 'inspector' && manualDt) return aMedianoche(manualDt);
    return null;
  }, [selected, manualDay, manual, kind, manualDt]);
  const boardDayKey = boardDay ? boardDay.toISOString().slice(0, 10) : null;
  /**
   * El instante del datetime manual, o null si todavía está a medio escribir. Se usa para la
   * propuesta que se dibuja en el tablero del día: un `startMs` NaN no rompe nada de entrada, se
   * propaga por todo el cálculo del día y termina pintando basura en vez de simplemente no pintarse.
   */
  const manualMs = useMemo(() => {
    const ms = manualDt ? new Date(manualDt).getTime() : NaN;
    return Number.isNaN(ms) ? null : ms;
  }, [manualDt]);

  // Carga citas + pronóstico horario del día del slot para pintar el DayBoard.
  useEffect(() => {
    if (!boardDay || !resp?.claimLoc) { setBoardAppts([]); setForecast([]); return; }
    const from = new Date(boardDay); const to = new Date(boardDay.getTime() + 86400000);
    let alive = true;
    api.get<{ appointments: BoardAppt[] }>(`/api/appointments?from=${from.toISOString()}&to=${to.toISOString()}`)
      .then((r) => { if (alive) setBoardAppts(r.appointments || []); }).catch(() => { if (alive) setBoardAppts([]); });
    api.get<{ periods: Forecast[] }>(`/api/scheduling/claim-forecast?lat=${resp.claimLoc.lat}&lng=${resp.claimLoc.lng}&from=${from.toISOString()}&to=${to.toISOString()}`)
      .then((r) => { if (alive) setForecast(r.periods || []); }).catch(() => { if (alive) setForecast([]); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardDayKey, resp?.claimLoc?.lat, resp?.claimLoc?.lng]);

  // Selecciona un slot recomendado (no agenda todavía; abre el paso de confirmación).
  function pickSlot(s: Slot) {
    setSelected({ startIso: s.start, inspectorName: s.inspector_name, label: `${fmtDay(s.start)} · ${fmtTime(s.start)} · ${s.inspector_name}` });
  }
  // Click en una ventana verde del DayBoard → elige ese inspector + hora. En modo "Set a specific
  // date & time" rellena el datetime + inspector (se confirma con el botón); si no, abre confirmación.
  function pickBoard(name: string, start: Date) {
    if (manual) { setManualInsp(name); setManualDt(localInput(start)); return; }
    setSelected({ startIso: start.toISOString(), inspectorName: name, label: `${fmtDay(start.toISOString())} · ${fmtTime(start.toISOString())} · ${name}` });
  }
  // "Pick manually" de un día: abre el DAY-DISTRIBUTION board de ese día (columnas por
  // inspector con ventanas verdes, drive-time y "Won't fit"), como ClaimRoute. Click en una
  // columna/ventana → elige inspector + hora exacta (paso de confirmación).
  function pickManuallyForDay(dayIso: string) {
    const d = new Date(dayIso); d.setHours(0, 0, 0, 0);
    setSelected(null);
    setManual(false);
    setManualDay(d);
  }
  // Agenda una hora manual directamente (el botón ya es una acción explícita).
  function bookManual() {
    // Misma trampa que arriba: con la fecha a medio escribir esto tiraba RangeError y el botón
    // moría en silencio (el ErrorBoundary no atrapa handlers). Mejor decirlo.
    const d = new Date(manualDt);
    if (Number.isNaN(d.getTime())) { setErr(t('sch.badDate')); return; }
    doBook({ startIso: d.toISOString(), inspectorName: kind === 'inspector' ? manualInsp : undefined });
  }

  async function cancelForNow() {
    if (!appointmentId) return;
    setBusy(true); setErr(null);
    try { await api.post(`/api/appointments/${appointmentId}/reschedule`, { mode: 'later', reason: reason || 'cancel_for_now', cause: cause || null, urgent }); onDone(); }
    catch (e) { setErr((e as Error).message || 'error'); setBusy(false); }
  }

  const noteText = (n: string | null) => {
    if (n === 'no_geo') return t('sch.noGeo');
    if (n === 'no_inspectors') return t('sch.noInspectors');
    if (n === 'none_this_week') return t('sch.noneThisWeek');
    return null;
  };

  if (booked) {
    return (
      <div className="sm-back" onClick={onClose}>
        <div className="schm" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()} ref={dialogo.ref} {...dialogo.props}>
          <div className="sm-success">
            <div className="sm-success-check"><Check size={30} strokeWidth={2.5} /></div>
            <div className="sm-success-title">{mode === 'reschedule' ? t('sch.rescheduledOk') : t('sch.bookedOk')}</div>
            {selected && <div className="sm-success-sub">{selected.label}</div>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sm-back" onClick={onClose}>
      <div className="schm" onClick={(e) => e.stopPropagation()} ref={dialogo.ref} {...dialogo.props}>
        <div className="sm-head">
          <div>
            <div className="sm-title">{mode === 'reschedule' ? t('sch.rescheduleTitle') : t('sch.title')} — {clientName}</div>
            {resp?.address && <div className="sm-addr"><MapPin size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {resp.address}</div>}
            <div className="sm-sub">{t('sch.subtitle')}</div>
          </div>
          <button className="sm-x" onClick={onClose}><X size={14} strokeWidth={1.75} /></button>
        </div>

        {/* Tabs de asignado (solo book) */}
        {mode === 'book' && (
          <div className="sm-tabs">
            {(['inspector', 'contractor', 'none'] as Kind[]).map((k) => (
              <button key={k} className={`sm-tab${kind === k ? ' on' : ''}`} onClick={() => setKind(k)}>
                {t(`sch.kind.${k}`)}{k === 'contractor' && resp?.contractorName ? ` · ${resp.contractorName}` : ''}
              </button>
            ))}
          </div>
        )}

        {/* Contractor / none: solo picker manual */}
        {mode === 'book' && kind === 'contractor' && (
          <input className="input" placeholder={t('sch.contractorName')} value={contractorName} onChange={(e) => setContractorName(e.target.value)} style={{ marginBottom: 8 }} />
        )}

        {mode === 'reschedule' && (
          <div style={{ display: 'grid', gap: 8, marginBottom: 8 }}>
            {urgent && (
              <div className="badge red" style={{ justifySelf: 'start', fontWeight: 'var(--fw-bold)' }} title={t('sch.urgentHint')}>🚨 {t('sch.urgentTag')} — {t('sch.urgentHint')}</div>
            )}
            <select className="input" value={cause} onChange={(e) => setCause(e.target.value)} style={{ maxWidth: 220 }}>
              <option value="">{t('sch.causePh')}</option>
              {['inspector', 'homeowner', 'weather', 'office', 'other'].map((c) => <option key={c} value={c}>{t(`sch.cause.${c}`)}</option>)}
            </select>
            {/* Nota COMPLETA (feedback 2026-07-30): la causa preset sola no alcanza — acá va la
                explicación completa, y el backend guarda AMBAS en el reporte del claim. */}
            <textarea className="input" rows={3} placeholder={t('sch.reasonFull')} value={reason}
              onChange={(e) => setReason(e.target.value)} style={{ width: '100%', resize: 'vertical' }} />
          </div>
        )}

        {/* Toggle manual + (reschedule) cancel for now */}
        <div className="sm-toggles">
          <label className="sm-check"><input type="checkbox" checked={manual} onChange={(e) => { setManual(e.target.checked); setCancelLater(false); }} /> {t('sch.manual')}</label>
          {mode === 'reschedule' && <label className="sm-check"><input type="checkbox" checked={cancelLater} onChange={(e) => { setCancelLater(e.target.checked); setManual(false); }} /> {t('sch.cancelForNow')}</label>}
        </div>

        {cancelLater ? (
          <div className="sm-manual">
            <p className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{t('sch.cancelForNowHint')}</p>
            {err && <div className="sm-err">{err}</div>}
            <button className="btn primary" disabled={busy} onClick={cancelForNow}>{t('sch.confirmCancel')}</button>
          </div>
        ) : manual || (kind !== 'inspector' && mode === 'book') ? (
          <div className="sm-manual">
            {kind === 'inspector' && (
              <label className="sm-field"><span>{t('sch.inspector')}</span>
                <select className="input" value={manualInsp} onChange={(e) => setManualInsp(e.target.value)}>
                  {inspectors.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
            )}
            <label className="sm-field"><span>{t('sch.dateTime')}</span>
              <input className="input" type="datetime-local" value={manualDt} onChange={(e) => setManualDt(e.target.value)} />
            </label>
            {err && <div className="sm-err">{err}</div>}
            <button className="btn primary" disabled={busy} onClick={bookManual}>
              {busy ? t('common.loadingDots') : (mode === 'reschedule' ? t('sch.confirmReschedule') : t('sch.confirmBook'))}
            </button>
            {/* Calendario del día elegido: distribución por inspector (ventanas verdes + drive-time
                + "Won't fit"). Click en una ventana rellena la hora + inspector de arriba. */}
            {kind === 'inspector' && boardDay && resp?.claimLoc && inspectors.length > 0 && (
              <div className="schm-board">
                <div className="schm-board-head">{t('sch.dayDistribution')} — {fmtDay(boardDay.toISOString())}<span className="schm-board-hint"> · {t('sch.clickWindow')}</span></div>
                <ScheduleDayBoard
                  day={boardDay} inspectors={inspectors} appts={boardAppts} forecast={forecast}
                  scheduleClaim={{ lat: resp.claimLoc.lat, lng: resp.claimLoc.lng, durationMin: resp.durationMin ?? 60 }}
                  proposed={manualMs != null ? { startMs: manualMs, endMs: manualMs + (resp.durationMin ?? 60) * 60000, lat: resp.claimLoc.lat, lng: resp.claimLoc.lng, inspectorName: manualInsp } : null}
                  onPick={pickBoard}
                />
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Nav de semana */}
            <div className="sm-week">
              <button className="sm-week-nav" disabled={weekOffset === 0} onClick={() => { setManualDay(null); setSelected(null); setWeekOffset((w) => Math.max(0, w - 1)); }}>‹</button>
              <span className="sm-week-label">{weekLabel}</span>
              <button className="sm-week-nav" disabled={weekOffset >= 8} onClick={() => { setManualDay(null); setSelected(null); setWeekOffset((w) => Math.min(8, w + 1)); }}>›</button>
            </div>

            {loading ? (
              <div className="sm-empty">{t('common.loadingDots')}</div>
            ) : fallo ? (
              <NoCargo onRetry={load} />
            ) : resp?.note ? (
              <div className="sm-empty">{noteText(resp.note)}</div>
            ) : (
              <div className="sm-days">
                {byDay.map((d) => (
                  <div key={d.key} className="sm-day">
                    <div className="sm-day-head">
                      <span>{fmtDay(d.iso)}</span>
                      <button type="button" className="sm-day-manual" onClick={() => pickManuallyForDay(d.iso)}>{t('sch.pickManually')}</button>
                    </div>
                    {d.slots.length === 0 ? (
                      <div className="sm-day-empty">{t('sch.noSlots')}</div>
                    ) : (
                      <div className="sm-slots">
                        {d.slots.map((s) => {
                          const wet = s.rain_pct >= rainThreshold;
                          const on = selected?.startIso === s.start && selected?.inspectorName === s.inspector_name;
                          return (
                            <button key={`${s.start}:${s.inspector_name}`} className={`sm-slot${wet ? ' wet' : ''}${on ? ' on' : ''}`} disabled={busy} onClick={() => pickSlot(s)}>
                              <div className="sm-slot-top">
                                <span className="sm-slot-time">{s.weather_icon ?? <CalendarDays size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} />} {fmtTime(s.start)}</span>
                                <span className="sm-slot-insp">{s.inspector_name}</span>
                              </div>
                              <div className="sm-slot-meta">
                                {s.rain_pct > 0 && <span className={wet ? 'rain-hi' : ''}><Umbrella size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {s.rain_pct}%</span>}
                                {s.weather_short && <span>{s.weather_short}</span>}
                                {s.added_miles > 0 && <span>+{s.added_miles}mi</span>}
                                {s.drive_in_min > 0 && <span>{s.drive_in_min}min drive</span>}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {err && <div className="sm-err">{err}</div>}

            {/* DayBoard: distribución por inspector del día (ventanas verdes + drive-time +
                "Won't fit"). Aparece al elegir un slot recomendado O al darle "Pick manually"
                a un día. Sin slot elegido muestra las ventanas libres; con slot, el ghost. */}
            {boardDay && resp?.claimLoc && inspectors.length > 0 && (
              <div className="schm-board">
                <div className="schm-board-head">
                  {t('sch.dayDistribution')} — {fmtDay(boardDay.toISOString())}<span className="schm-board-hint"> · {t('sch.clickWindow')}</span>
                  {manualDay && !selected && (
                    <button type="button" className="sm-day-manual" style={{ marginLeft: 'auto' }} onClick={() => setManualDay(null)}><X size={12} strokeWidth={1.75} /></button>
                  )}
                </div>
                <ScheduleDayBoard
                  day={boardDay} inspectors={inspectors} appts={boardAppts} forecast={forecast}
                  scheduleClaim={{ lat: resp.claimLoc.lat, lng: resp.claimLoc.lng, durationMin: resp.durationMin ?? 60 }}
                  proposed={selected ? { startMs: new Date(selected.startIso).getTime(), endMs: new Date(selected.startIso).getTime() + (resp.durationMin ?? 60) * 60000, lat: resp.claimLoc.lat, lng: resp.claimLoc.lng, inspectorName: selected.inspectorName || manualInsp } : null}
                  onPick={pickBoard}
                />
              </div>
            )}

            {/* Barra de confirmación del slot elegido */}
            {selected && (
              <div className="sm-confirm">
                <div className="sm-confirm-info">
                  <span className="sm-confirm-label">{mode === 'reschedule' ? t('sch.rescheduleTo') : t('sch.bookFor')}</span>
                  <b>{selected.label}</b>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn ghost sm" disabled={busy} onClick={() => setSelected(null)}>{t('common.cancel')}</button>
                  <button className="btn primary sm" disabled={busy} onClick={() => doBook(selected)}>
                    {busy ? t('common.loadingDots') : (mode === 'reschedule' ? t('sch.confirmReschedule') : t('sch.confirmBook'))}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
